import { moveItem } from "#src/utils/moveItem";
import {
  Button,
  DataTable,
  ItemAction,
  Link,
  Panel,
  useFieldArray,
  useFormContext,
  type DataTableProps,
} from "@validereinc/common-components";
import {
  FormSchemaType,
  type FormSchemaQuestionDisplayType,
} from "@validereinc/domain";
import {
  ReorderDirection,
  toTitleCaseString,
  type ReorderDirectionType,
} from "@validereinc/utilities";
import React from "react";
import { v4 as uuid } from "uuid";
import { CreateFormTemplateAddQuestionDrawer } from "./CreateFormTemplateAddQuestionDrawer";
import { DeleteQuestionDialog } from "./DeleteQuestionDialog";
import { useFormTemplateQuestionDrawer } from "./useFormTemplateQuestionDrawer";

export const CreateFormTemplateQuestionTable = ({
  formFieldSectionQuestionsName,
  sectionName,
}: {
  formFieldSectionQuestionsName: string;
  sectionName: string;
}) => {
  // formFieldSectionQuestionsName is in the shape of config.sections.<section_number>.questions, where <section_number> starts at 0
  const sectionNumber =
    parseInt(formFieldSectionQuestionsName.split(".")[2]) + 1;

  const parentForm = useFormContext();

  const { move } = useFieldArray({
    control: parentForm.control,
    name: formFieldSectionQuestionsName,
  });

  const sectionQuestionIds = parentForm.watch(formFieldSectionQuestionsName);
  const sections = parentForm.watch(
    "config.sections"
  ) as FormSchemaType["config"]["sections"];
  const questions = parentForm.watch("config.questions");

  const allDisplayQuestions = sections.flatMap((section, sectionIndex) => {
    const questionIds = section.questions;
    return questionIds.reduce(
      (
        total: FormSchemaQuestionDisplayType[],
        questionId: string,
        index: number
      ) => {
        const questionDetails: FormSchemaQuestionDisplayType = {
          question_id: questionId,
          type: null,
          section_number: sectionIndex + 1,
          question_number: questions[questionId]?.question_number ?? index + 1,
          ...(questions[questionId] ? questions[questionId] : {}),
        };

        if (!questionDetails) {
          return total;
        } else {
          return [...total, questionDetails];
        }
      },
      [] as FormSchemaQuestionDisplayType[]
    );
  });

  const displayQuestions: FormSchemaQuestionDisplayType[] =
    allDisplayQuestions.filter(
      (question) => question.section_number === sectionNumber
    );

  const {
    isAddQuestionDrawerOpen,
    questionIdToDelete,
    questionToEdit,
    setQuestionIdToDelete,
    setQuestionToEdit,
    handleDeleteQuestion,
    onCloseAddQuestionDrawer,
    onOpenAddQuestionDrawer,
    onSaveQuestionDrawer,
  } = useFormTemplateQuestionDrawer(
    formFieldSectionQuestionsName,
    displayQuestions
  );

  const moveQuestion = (
    indexToSwapFrom: number,
    direction: ReorderDirectionType
  ) => {
    const { originalItemId, adjacentItemId, indexToMoveTo } = moveItem(
      sectionQuestionIds,
      move,
      indexToSwapFrom,
      direction
    );

    if (originalItemId && adjacentItemId && indexToMoveTo !== undefined) {
      const newQuestions = { ...questions };
      newQuestions[originalItemId].question_number = indexToMoveTo + 1;
      newQuestions[adjacentItemId].question_number = indexToSwapFrom + 1;
      parentForm.setValue("config.questions", { ...newQuestions });
    }
  };
  const handleDuplicateQuestion = (
    questionToDuplicate: FormSchemaQuestionDisplayType
  ) => {
    const currentQuestions = parentForm.getValues("config.questions");
    const numberOfExistingCopies = Object.values(
      currentQuestions as Record<string, FormSchemaQuestionDisplayType>
    ).filter((question) =>
      question.prompt.startsWith(questionToDuplicate.prompt)
    ).length;

    const duplicatedQuestion = structuredClone(questionToDuplicate);
    const newQuestionId = uuid().split("-").join("");
    const newQuestionNumber = Object.keys(currentQuestions).length + 1;
    const newPrompt = `${questionToDuplicate.prompt} (${numberOfExistingCopies + 1})`;

    parentForm.setValue("config.questions", {
      ...currentQuestions,
      [newQuestionId]: {
        ...duplicatedQuestion,
        question_id: newQuestionId,
        question_number: newQuestionNumber,
        prompt: newPrompt,
      },
    });
    parentForm.setValue(formFieldSectionQuestionsName, [
      ...sectionQuestionIds,
      newQuestionId,
    ]);
  };

  const getItemActions = ({
    item,
  }: {
    item: FormSchemaQuestionDisplayType;
  }): ItemAction[] => {
    const questionIndex = item.question_number - 1;
    return [
      {
        label: "Duplicate",
        buttonProps: {
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            handleDuplicateQuestion(item);
          },
          icon: "copy",
        },
      },
      ...(questionIndex !== 0
        ? ([
            {
              label: "Move Up",
              buttonProps: {
                onClick: (
                  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  e.stopPropagation();
                  moveQuestion(questionIndex, ReorderDirection.UP);
                },
                icon: "arrow-circle-up",
              },
            },
          ] satisfies ItemAction[])
        : []),
      ...(questionIndex !== displayQuestions.length - 1
        ? ([
            {
              label: "Move Down",
              buttonProps: {
                onClick: (
                  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  e.stopPropagation();
                  moveQuestion(questionIndex, ReorderDirection.DOWN);
                },
                icon: "arrow-circle-down",
              },
            },
          ] satisfies ItemAction[])
        : []),
      ...([
        {
          label: "Delete",
          buttonProps: {
            onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              setQuestionIdToDelete(item.question_id);
            },
            icon: "trash",
            className: "button--error-outline",
          },
        },
      ] satisfies ItemAction[]),
    ];
  };

  const onEditQuestion: DataTableProps<FormSchemaQuestionDisplayType>["onRowClick"] =
    (question) => {
      setQuestionToEdit(question);
      onOpenAddQuestionDrawer();
    };

  const CreateQuestionButton = () => (
    <Button
      icon="plus-circle"
      onClick={onOpenAddQuestionDrawer}
    >
      Create Question
    </Button>
  );

  return (
    <>
      <Panel
        title={`Section ${sectionNumber}: ${sectionName} Questions `}
        actionRow={
          displayQuestions?.length
            ? [<CreateQuestionButton key="create-question-button" />]
            : undefined
        }
      >
        <DataTable
          showRowNumbers
          headers={[
            {
              key: "prompt",
              label: "Prompt",
              renderComponent: ({ item }) => (
                <Link
                  label={item.prompt}
                  onClick={() => onEditQuestion(item)}
                />
              ),
            },
            {
              key: "description",
              label: "Description",
              renderComponent: ({ item }) => (
                <DataTable.DataRow.TextCell value={item.description} />
              ),
            },
            {
              key: "type",
              label: "Question Type",
              renderComponent: ({ item }) =>
                toTitleCaseString(item.type, { intent: "ui" }),
            },
          ]}
          getItemId={(item) => item.question_id}
          items={displayQuestions}
          emptyStateProps={{
            title: "There are no questions in this section",
            suggestion: 'Press the "Create Question" button to add a question',
            action: <CreateQuestionButton />,
          }}
          getItemActions={getItemActions}
          onRowClick={onEditQuestion}
        />
      </Panel>

      <CreateFormTemplateAddQuestionDrawer
        isOpen={isAddQuestionDrawerOpen}
        onClose={onCloseAddQuestionDrawer}
        onSave={onSaveQuestionDrawer}
        questionToEdit={questionToEdit}
        isDeleteQuestionDialogOpen={!!questionIdToDelete}
        handleDeleteQuestion={() => {
          setQuestionIdToDelete(questionToEdit?.question_id ?? "");
        }}
        sectionNumber={sectionNumber}
        sectionQuestionsLength={sectionQuestionIds.length}
        allDisplayQuestions={allDisplayQuestions}
      />
      <DeleteQuestionDialog
        isOpen={!!questionIdToDelete}
        onClose={() => {
          setQuestionIdToDelete("");
        }}
        handleDeleteQuestion={handleDeleteQuestion}
        questionIdToDelete={questionIdToDelete}
      />
    </>
  );
};
